import {defineStore} from "pinia";
import type {Document} from "@/api/models/documents/Document";
import type {GetDocumentsResponse} from "@/api/models/documents/GetDocumentsResponse";
import type {UploadDocumentResponse} from "@/api/models/documents/UploadDocumentResponse";
import ApiClient from "@/api/client/ApiClient";
import type {ApiMessage} from "@/api/models/common/ApiResponse";
import {handleApiError} from "@/api/helper";

export const useDocumentsStore = defineStore({
  id: "documents",

  state() {
    return {
      documentsByDevice: {} as Record<number, Document[]>,
    };
  },

  actions: {
    async getDocumentsByDeviceId(deviceId: number, force = false): Promise<Document[]> {
      if (!force && this.documentsByDevice[deviceId]) {
        return this.documentsByDevice[deviceId];
      }

      try {
        const response = await ApiClient.getJson<GetDocumentsResponse>(`Document/list/${deviceId}`);
        if (!response.success) {
          throw new Error(response.message);
        }

        this.documentsByDevice[deviceId] = response.documents || [];
        return this.documentsByDevice[deviceId];
      } catch (e: unknown) {
        console.error("Error fetching documents:", e);
        throw e;
      }
    },

    async uploadDocument(deviceId: number, file: File): Promise<ApiMessage> {
      try {
        const formData = new FormData();
        formData.append("DeviceId", deviceId.toString());
        formData.append("File", file);

        const response = await fetch(
          `${process.env.VUE_APP_API_ENDPOINT}Document/upload`,
          {
            method: "POST",
            body: formData,
            credentials: "include",
          }
        );

        const result: UploadDocumentResponse = await response.json();

        if (result.success && result.document) {
          if (!this.documentsByDevice[deviceId]) {
            this.documentsByDevice[deviceId] = [];
          }
          this.documentsByDevice[deviceId].unshift(result.document);
        }

        return result;
      } catch (e: unknown) {
        return await handleApiError(e as Error);
      }
    },

    async deleteDocument(documentId: number, deviceId: number): Promise<ApiMessage> {
      try {
        const response = await ApiClient.deleteJson(`Document/delete/${documentId}`);

        if (response.success) {
          if (this.documentsByDevice[deviceId]) {
            this.documentsByDevice[deviceId] = this.documentsByDevice[deviceId].filter(
              (doc) => doc.id !== documentId
            );
          }
        }

        return response;
      } catch (e: unknown) {
        return await handleApiError(e as Error);
      }
    },

    getDownloadUrl(documentId: number): string {
      return `${process.env.VUE_APP_API_ENDPOINT}Document/download/${documentId}`;
    },

    clearDocumentsForDevice(deviceId: number): void {
      delete this.documentsByDevice[deviceId];
    },
  },
});
